// Bank-feed plan entitlements in the UI: the upsell card, "Paused" status and
// the Sync now limit. Everything shown comes from the server's
// /plaid/entitlement and /plaid/items answers (config/plan-settings.json).
import React from 'react';

const SYNC_TEXT = { realtime: 'real-time sync', weekly: 'weekly sync' };
const ET = { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' };

export function planLine(p) {
  const n = p.max_connections;
  const price = p.monthly_price ? ` ($${p.monthly_price}/month)` : '';
  return `${p.name}${price}: ${SYNC_TEXT[p.sync] || p.sync}, up to ${n} bank connection${n === 1 ? '' : 's'}`;
}

// What the card says, or null when nothing needs saying.
export function planNotice(ent) {
  if (!ent) return null;
  const upgrades = (ent.upgrade_plans || []).filter((p) => !ent.enabled || p.max_connections > ent.max_connections || (ent.sync === 'weekly' && p.sync === 'realtime'));
  if (!ent.enabled && ent.addon) {
    // This plan can add bank feeds on its own: offer that, not a plan change.
    return { tone: 'upsell', title: "Bank feeds aren't included in this location's plan",
      body: `Add them for $${ent.addon.monthly_price}/month: ${SYNC_TEXT[ent.addon.sync] || ent.addon.sync}, up to ${ent.addon.max_connections} bank connection${ent.addon.max_connections === 1 ? '' : 's'}. Transactions import automatically.`,
      upgrades: [], addon: ent.addon };
  }
  if (!ent.enabled) {
    return { tone: 'upsell', title: "Bank feeds aren't included in this location's plan",
      body: 'Connect your bank so transactions import automatically.', upgrades };
  }
  if (ent.reason === 'connections') {
    return { tone: 'limit', title: `All ${ent.max_connections} bank connection${ent.max_connections === 1 ? '' : 's'} on this plan are in use`,
      body: 'Disconnect one, or upgrade for more connections.', upgrades };
  }
  if (ent.reason === 'platform') {
    return { tone: 'limit', title: 'New bank connections are not available right now', body: 'Please try again later.', upgrades: [] };
  }
  if (ent.sync === 'weekly') {
    return { tone: 'info', title: 'This plan syncs your banks once a week',
      body: ent.manual_sync?.per_week ? `Sync now can be used ${ent.manual_sync.per_week} time${ent.manual_sync.per_week === 1 ? '' : 's'} a week per connection.` : '', upgrades };
  }
  return null;
}

export function addonLabel(addon) {
  return `Add ${String(addon.name || 'bank feeds').toLowerCase()} $${String(addon.monthly_price).replace(/\.00$/, '')}/mo`;
}

export function PlanNotice({ entitlement, onAddAddon, busy }) {
  const n = planNotice(entitlement);
  if (!n) return null;
  return (
    <section data-testid="plan-notice" style={{ border: `1px solid ${n.tone === 'info' ? '#d1d5db' : '#b45309'}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
      <strong>{n.title}</strong>
      {n.body && <div style={{ marginTop: 4 }}>{n.body}</div>}
      {n.upgrades.length > 0 && (
        <ul style={{ margin: '8px 0 0 16px' }}>{n.upgrades.map((p) => <li key={p.key}>{planLine(p)}</li>)}</ul>
      )}
      {n.upgrades.length > 0 && <div style={{ marginTop: 8 }}><a href="/billing">Upgrade on the Billing page</a></div>}
      {n.addon && (
        <div style={{ marginTop: 8 }}>
          {onAddAddon
            ? <button type="button" data-testid="add-bank-feeds" disabled={!!busy} onClick={() => onAddAddon(n.addon)}>{addonLabel(n.addon)}</button>
            : <a href="/billing">{addonLabel(n.addon)}</a>}
        </div>
      )}
    </section>
  );
}

export function pausedText(item) {
  if (!item || !item.paused) return null;
  return item.paused_reason === 'connections'
    ? "Paused: over this plan's connection limit. Data is kept; disconnect another bank or upgrade to resume."
    : "Paused: this location's plan doesn't include bank feeds. Data is kept; upgrade to resume (no need to reconnect).";
}

// Label and state of the Sync now button for one connection.
export function syncButton(item, now = new Date()) {
  const m = item?.manual_sync;
  if (!m) return { label: 'Sync now', disabled: false, title: undefined };
  if (item.paused) return { label: 'Sync now', disabled: true, title: pausedText(item) };
  // A used-up allowance or a cooldown does not silently ignore clicks: the
  // button stays clickable (cooldown: true) and says how long is left; the
  // page shows `wait` on that bank instead of calling the server.
  if (m.per_week) {
    if (m.allowed) return { label: `Sync now (${m.remaining} left this week)`, disabled: false, title: undefined };
    const wait = 'Sync now is used up for this week for this bank. It syncs automatically once a week.';
    return { label: `Sync now (${m.remaining} left this week)`, disabled: false, cooldown: true, title: wait, wait };
  }
  if (!m.allowed && m.retry_after && new Date(m.retry_after) > now) {
    const at = new Date(m.retry_after).toLocaleTimeString('en-US', ET);
    const mins = Math.max(1, Math.ceil((new Date(m.retry_after).getTime() - now.getTime()) / 60000));
    const left = mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} min` : `${mins} min`;
    return { label: `Sync now (in ${left})`, disabled: false, cooldown: true, title: `Available again at ${at} ET`,
      wait: `Sync now was just used for this bank. It is available again in ${left} (at ${at} ET).` };
  }
  return { label: 'Sync now', disabled: false, title: undefined };
}